import * as Sentry from "@sentry/react";
import { routes } from "../../routing/routes";
import { Link, useNavigate } from "react-router-dom";
import { FormWrapper } from "../../components/form-wrapper";
import PrivacyImage from "./assets/privacy.svg?react";
import { Info } from "../../components/info";
import {
  LockClosedIcon,
  PaperAirplaneIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { useFormState } from "../../components/context/hooks/useFormState";
import { toast } from "react-toastify";
import { Checkbox } from "component-library";
import { useTranslation, Trans } from "react-i18next";

const SubmitButtonContent: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      {t("pages.send.submit")} <PaperAirplaneIcon width={15} />
    </>
  );
};

// NOTE: This component uses a plain Checkbox instead of react-hook-form's ControlledCheckbox
// to avoid race conditions. Since updateFormState is async and the send() function reads
// from formState, using react-hook-form would require useEffect synchronization which
// is an anti-pattern. Instead, we manage the checkbox state directly in the global form state.
export const SendPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { send, updateFormState, formState } = useFormState();
  const [hasError, setHasError] = useState(false);
  const { t } = useTranslation();

  const onSubmit = () => {
    if (!formState.privacyPolicyAccepted) {
      setHasError(true);
      return;
    }

    setIsLoading(true);

    send()
      .then(() => {
        navigate(routes.questionnaire.done);
      })
      .catch((error) => {
        toast.error(t("pages.send.errors.unexpected"), {
          autoClose: 10000,
          position: "top-center",
        });

        Sentry.captureException(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const toggleCheckbox = () => {
    const currentPrivacy = formState.privacyPolicyAccepted || false;

    updateFormState({ privacyPolicyAccepted: !currentPrivacy });
  };

  return (
    <FormWrapper
      onSubmit={onSubmit}
      SubmitButtonContent={SubmitButtonContent}
      isLoading={isLoading}
    >
      <div className="flex flex-col items-center gap-7 my-4">
        <span className="text-2xl font-light text-text-primary text-center">
          {t("pages.send.title")}
        </span>
        <PrivacyImage className="max-w-full w-[279px] text-primary" />
        <div className="flex flex-col gap-4 w-full">
          <Info Icon={LockClosedIcon}>{t("pages.send.encrypted")}</Info>
          <Info Icon={ShieldCheckIcon}>{t("pages.send.private")}</Info>
        </div>
      </div>
      <div className="mt-10 w-full">
        <Checkbox
          onChange={toggleCheckbox}
          value={formState.privacyPolicyAccepted || false}
          hasError={hasError}
        >
          <span className="text-base font-normal">
            <Trans
              i18nKey="pages.send.privacy_policy_accepted"
              components={{
                privacyLink: (
                  <Link
                    className="underline"
                    target="_blank"
                    to={routes.privacy}
                  />
                ),
              }}
            />
          </span>
        </Checkbox>
      </div>
    </FormWrapper>
  );
};
